/**
 * Shared design tokens and primitive React components.
 * No external dependencies — plain CSS-in-JS objects only.
 * Usage: import { Card, Button, Badge, tokens } from "../lib/ui";
 */

// ── Design Tokens ──────────────────────────────────────────────────────────────

export const tokens = {
  typography: {
    display: '"Fraunces", "Iowan Old Style", "Palatino Linotype", serif',
    body: '"Manrope", "Avenir Next", "Segoe UI", sans-serif',
  },
  radius: {
    sm: 8,
    md: 14,
    lg: 18,
    xl: 24,
    full: 9999,
  },
  space: {
    xs: "0.4rem",
    sm: "0.75rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem",
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  fontSize: {
    xs: "0.75rem",
    sm: "0.875rem",
    md: "1rem",
    lg: "1.1rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "2rem",
    "4xl": "2.5rem",
  },
  color: {
    primary: "#1d4ed8",
    primaryLight: "#eff6ff",
    primaryDark: "#1e40af",
    primaryGlow: "rgba(29, 78, 216, 0.16)",
    accent: "#14532d",
    accentLight: "#f0fdf4",
    accentDeep: "#16351f",
    gold: "#a16207",
    goldLight: "#fffbeb",
    sand: "#f8f4ea",
    ink: "#172033",
    plum: "#5b4b8a",
    mist: "#edf4ff",
    orange: "#f97316",
    orangeLight: "#fff7ed",
    surface: "#ffffff",
    surfaceGlass: "rgba(255,255,255,0.72)",
    surfaceAlt: "#fafaf9",
    border: "#e5e7eb",
    borderFocus: "#93c5fd",
    muted: "#6b7280",
    mutedLight: "#f3f4f6",
    error: "#b91c1c",
    errorLight: "#fef2f2",
    success: "#14532d",
    successLight: "#f0fdf4",
  },
  shadow: {
    card: "0 10px 30px rgba(0,0,0,0.05)",
    modal: "0 18px 48px rgba(0,0,0,0.18)",
    sm: "0 2px 8px rgba(0,0,0,0.06)",
    glow: "0 24px 80px rgba(23, 32, 51, 0.14)",
    inset: "inset 0 1px 0 rgba(255,255,255,0.45)",
  },
} as const;

// ── Primitive Styles (CSSProperties objects) ──────────────────────────────────

export const panelStyle: React.CSSProperties = {
  background: tokens.color.surfaceGlass,
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: `1px solid rgba(255,255,255,0.56)`,
  borderRadius: tokens.radius.lg,
  padding: tokens.space.md,
  boxShadow: `${tokens.shadow.card}, ${tokens.shadow.inset}`,
};

export const subtleText: React.CSSProperties = {
  color: tokens.color.muted,
  margin: 0,
  fontSize: tokens.fontSize.sm,
  lineHeight: 1.6,
};

export const sectionTitle: React.CSSProperties = {
  margin: 0,
  fontSize: tokens.fontSize.lg,
  fontWeight: tokens.fontWeight.bold,
};

export const fieldStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.9rem 1rem",
  borderRadius: tokens.radius.md,
  border: `1.5px solid rgba(23,32,51,0.1)`,
  background: "rgba(255,255,255,0.92)",
  font: "inherit",
  fontSize: tokens.fontSize.md,
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.15s, box-shadow 0.15s, transform 0.15s",
};

// ── Button variants ────────────────────────────────────────────────────────────

function baseButton(
  variant: "primary" | "secondary" | "danger" | "ghost"
): React.CSSProperties {
  const map: Record<typeof variant, React.CSSProperties> = {
    primary: {
      background: "linear-gradient(135deg, #1d4ed8 0%, #1738b5 100%)",
      color: "#fff",
      border: "none",
      boxShadow: `0 16px 30px ${tokens.color.primaryGlow}`,
    },
    secondary: {
      background: "rgba(255,255,255,0.78)",
      color: tokens.color.ink,
      border: `1px solid rgba(23,32,51,0.12)`,
    },
    danger: {
      background: tokens.color.errorLight,
      color: tokens.color.error,
      border: `1px solid #fca5a5`,
    },
    ghost: {
      background: "transparent",
      color: tokens.color.primary,
      border: "none",
    },
  };
  return {
    ...map[variant],
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    borderRadius: tokens.radius.md,
    padding: "0.75rem 1.1rem",
    fontSize: tokens.fontSize.sm,
    fontWeight: tokens.fontWeight.semibold,
    cursor: "pointer",
    fontFamily: tokens.typography.body,
    letterSpacing: "0.01em",
    transition: "opacity 0.15s, transform 0.15s, box-shadow 0.15s",
  };
}

export const buttonStyles = {
  primary: baseButton("primary"),
  secondary: baseButton("secondary"),
  danger: baseButton("danger"),
  ghost: baseButton("ghost"),
} as const;

export const pageShellStyle: React.CSSProperties = {
  minHeight: "100vh",
  background: "radial-gradient(circle at top left, rgba(245, 158, 11, 0.16), transparent 22%), radial-gradient(circle at top right, rgba(59, 130, 246, 0.18), transparent 24%), linear-gradient(180deg, #fffdf8 0%, #f7fbff 36%, #f5f8ef 100%)",
  color: tokens.color.ink,
  fontFamily: tokens.typography.body,
};

export const heroPanelStyle: React.CSSProperties = {
  ...panelStyle,
  background: "linear-gradient(135deg, rgba(255,248,221,0.92) 0%, rgba(240,247,255,0.96) 48%, rgba(232,250,236,0.94) 100%)",
  border: "1px solid rgba(255,255,255,0.62)",
  boxShadow: `${tokens.shadow.glow}, ${tokens.shadow.inset}`,
};

export const eyebrowStyle: React.CSSProperties = {
  ...subtleText,
  textTransform: "uppercase",
  letterSpacing: "0.16em",
  fontWeight: tokens.fontWeight.bold,
  fontSize: tokens.fontSize.xs,
};

export const displayTitleStyle: React.CSSProperties = {
  margin: 0,
  fontFamily: tokens.typography.display,
  color: tokens.color.ink,
  letterSpacing: "-0.03em",
};

export const layoutWidthStyle: React.CSSProperties = {
  width: "min(1180px, calc(100% - 2rem))",
  margin: "0 auto",
};

// ── Badge chip (tier / status labels) ─────────────────────────────────────────

export function tierColor(tier: string): { background: string; color: string; border: string } {
  switch (tier) {
    case "vip":
      return { background: "#1c1917", color: "#fbbf24", border: "#78350f" };
    case "elite":
      return { background: "#f0fdf4", color: "#14532d", border: "#86efac" };
    case "premium":
      return { background: "#eff6ff", color: "#1d4ed8", border: "#93c5fd" };
    default:
      return { background: "#f3f4f6", color: "#374151", border: "#d1d5db" };
  }
}

export function statusColor(active: boolean): { background: string; color: string } {
  return active
    ? { background: "#f0fdf4", color: "#14532d" }
    : { background: "#f3f4f6", color: "#6b7280" };
}

// ── Badge definition map for gamification ─────────────────────────────────────

export const BADGE_META: Record<string, { emoji: string; label: string; description: string; color: string }> = {
  first_session: { emoji: "🌱", label: "First Step", description: "Logged your very first session", color: "#22c55e" },
  week_streak: { emoji: "🔥", label: "7-Day Streak", description: "Practiced every day for a week", color: "#f97316" },
  month_streak: { emoji: "🏆", label: "30-Day Streak", description: "Maintained practice for 30 days", color: "#eab308" },
  first_mood: { emoji: "🧘", label: "Mood Scout", description: "Completed your first mood check-in", color: "#8b5cf6" },
  hundred_minutes: { emoji: "⏱️", label: "Centurion", description: "Accumulated 100+ minutes of practice", color: "#0ea5e9" },
  early_bird: { emoji: "🌅", label: "Early Bird", description: "Practiced before 7 AM", color: "#f59e0b" },
  night_owl: { emoji: "🦉", label: "Night Owl", description: "Practiced after 10 PM", color: "#6366f1" },
};
